import {
  forwardRef,
  useMemo,
  type ChangeEvent,
  type ChangeEventHandler,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";

import { useFieldControl } from "../Field/FieldContext";
import { SliderMarks } from "./SliderMarks";
import { ControlFrame, Rail, RangeInput, Root, ValueLabel } from "./sliderStyles";
import {
  clamp,
  DEFAULT_MAX,
  DEFAULT_MIN,
  formatSliderValue,
  getGeneratedMarks,
  normalizeBounds,
  percentFromValue,
  thumbCenter,
  type SliderMark,
  type SliderSize,
  type SliderValueLabelDisplay,
  type SliderVariant,
} from "./sliderMath";

export type { SliderMark, SliderSize, SliderValueLabelDisplay, SliderVariant } from "./sliderMath";

export interface SliderProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "value" | "defaultValue" | "min" | "max" | "step" | "size" | "onChange"
> {
  value: number;
  onValueChange?: (value: number, event: ChangeEvent<HTMLInputElement>) => void;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  min?: number;
  max?: number;
  step?: number | "any";
  variant?: SliderVariant;
  trackSize?: SliderSize;
  thumbSize?: SliderSize;
  /**
   * tick 표시. `true`면 `step` 간격마다 tick 생성(점 = snap 정지점과 일치). 배열이면 지정 위치에만
   * tick/label. `step`(snap 간격)과 marks(표시)는 독립이라, 점을 snap과 맞추려면 `true`를 쓴다.
   */
  marks?: boolean | readonly SliderMark[];
  valueLabelDisplay?: SliderValueLabelDisplay;
  formatValue?: (value: number) => ReactNode;
  getAriaValueText?: (value: number) => string;
  fullWidth?: boolean;
}

/**
 * Single-value slider (native range input).
 * 라벨이 필요하면 `<Field>`로 감싸세요 — 자체 `label` prop은 없습니다.
 */
export const Slider = forwardRef<HTMLInputElement, SliderProps>(
  (
    {
      value,
      onValueChange,
      onChange,
      min: minProp = DEFAULT_MIN,
      max: maxProp = DEFAULT_MAX,
      step,
      variant = "continuous",
      trackSize = "md",
      thumbSize = "md",
      marks = false,
      valueLabelDisplay = "off",
      formatValue = formatSliderValue,
      getAriaValueText,
      fullWidth = true,
      disabled = false,
      className,
      style,
      id,
      ...rest
    },
    ref,
  ) => {
    const { controlId, disabled: isDisabled } = useFieldControl({
      componentName: "Slider",
      id,
      disabled,
    });
    const { min, max } = normalizeBounds(minProp, maxProp);
    const clampedValue = clamp(value, min, max);
    const resolvedStep = step ?? (variant === "discrete" ? 1 : "any");
    const progressPercent = percentFromValue(clampedValue, min, max);
    const progress = thumbCenter(progressPercent);
    const resolvedMarks = useMemo(
      () => getGeneratedMarks(marks, min, max, resolvedStep),
      [marks, max, min, resolvedStep],
    );
    const hasMarkLabels = resolvedMarks.some((mark) => mark.label !== undefined);
    const frameStyle = {
      ...style,
      "--c6-slider-mark-label-space": hasMarkLabels ? "1.3rem" : "0rem",
    } as CSSProperties;

    const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      const nextValue = Number(event.currentTarget.value);
      onValueChange?.(nextValue, event);
      onChange?.(event);
    };

    return (
      <Root $fullWidth={fullWidth} className={className} style={frameStyle}>
        <ControlFrame
          $disabled={isDisabled}
          $trackSize={trackSize}
          $thumbSize={thumbSize}
          $valueLabelDisplay={valueLabelDisplay}
          // 매 프레임 바뀌는 progress 는 inline CSS 변수로 — styled 템플릿 보간을 피해
          // 클래스 재생성/스타일시트 누적(누수)을 막는다.
          style={{ ["--c6-slider-progress" as string]: progress } as CSSProperties}
        >
          <Rail aria-hidden="true" />
          {valueLabelDisplay !== "off" ? (
            <ValueLabel
              htmlFor={controlId}
              $display={valueLabelDisplay}
              style={{ left: progress }}
              aria-hidden="true"
            >
              {formatValue(clampedValue)}
            </ValueLabel>
          ) : null}
          <RangeInput
            ref={ref}
            id={controlId}
            value={clampedValue}
            min={min}
            max={max}
            step={resolvedStep}
            disabled={isDisabled}
            aria-valuetext={getAriaValueText?.(clampedValue)}
            onChange={handleChange}
            {...rest}
          />
          <SliderMarks
            marks={resolvedMarks}
            min={min}
            max={max}
            disabled={isDisabled}
            isActive={(markValue) => markValue <= clampedValue}
          />
        </ControlFrame>
      </Root>
    );
  },
);

Slider.displayName = "Slider";
